import { useEffect, useState, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster } from "sonner";
import { Menu } from "lucide-react";
import Sidebar from "@/components/Sidebar";
import Dashboard from "@/pages/Dashboard";
import LeadEntry from "@/pages/LeadEntry";
import LeadLibrary from "@/pages/LeadLibrary";
import Products from "@/pages/Products";
import ShareKit from "@/pages/ShareKit";
import Orders from "@/pages/Orders";
import Reports from "@/pages/Reports";
import Commission from "@/pages/Commission";
import Settings from "@/pages/Settings";
import Login from "@/auth/Login";
import { AuthContext } from "@/auth/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

function App() {
  const [user, setUser] = useState(undefined); // undefined = checking, null = signed out
  const [authError, setAuthError] = useState("");
  const [view, setView] = useState("dashboard");
  const [leads, setLeads] = useState([]);
  const [team, setTeam] = useState([]);
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [libWonMonth, setLibWonMonth] = useState(null);

  // ---- Auth bootstrap (Emergent Google Sign-in) ----
  useEffect(() => {
    const hash = window.location.hash || "";
    if (hash.includes("session_id=")) {
      const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
      (async () => {
        try {
          const r = await axios.post(`${API}/auth/session`, { session_id: sid });
          window.history.replaceState(null, "", window.location.pathname);
          setUser(r.data);
        } catch (e) {
          window.history.replaceState(null, "", window.location.pathname);
          const msg =
            e?.response?.status === 403
              ? e.response.data?.detail ||
                "Access is restricted to @techbornventures.com accounts."
              : "Sign-in failed. Please try again.";
          setAuthError(msg);
          setUser(null);
        }
      })();
    } else {
      (async () => {
        try {
          const r = await axios.get(`${API}/auth/me`);
          setUser(r.data);
        } catch {
          setUser(null);
        }
      })();
    }
  }, []);

  const refetchAll = useCallback(async () => {
    try {
      const [l, t, p, s] = await Promise.all([
        axios.get(`${API}/leads`),
        axios.get(`${API}/team`),
        axios.get(`${API}/products`),
        axios.get(`${API}/dashboard/stats`),
      ]);
      setLeads(l.data);
      setTeam(t.data);
      setProducts(p.data);
      setStats(s.data);
    } catch (e) {
      console.error("Failed to load", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) refetchAll();
  }, [user, refetchAll]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {
      /* ignore */
    }
    setUser(null);
    setAuthError("");
    setView("dashboard");
  };

  if (user === undefined) {
    return (
      <div className="h-screen flex items-center justify-center bg-slate-50 text-slate-500 font-body">
        Loading Techborn Data Center…
      </div>
    );
  }

  if (!user) {
    return <Login error={authError} />;
  }

  const isOwner = user.role === "owner";

  return (
    <AuthContext.Provider value={{ user, isOwner, logout }}>
      <div className="flex h-screen overflow-hidden bg-slate-50 font-body">
        <Toaster position="top-right" richColors />
        <Sidebar
          view={view}
          setView={setView}
          leadsCount={leads.length}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
          user={user}
          isOwner={isOwner}
          onLogout={logout}
        />
        <main className="flex-1 flex flex-col overflow-y-auto">
          <div className="md:hidden flex items-center gap-3 px-4 h-14 border-b border-slate-200 bg-white sticky top-0 z-30">
            <button
              data-testid="mobile-menu-btn"
              onClick={() => setMobileOpen(true)}
              className="p-2 -ml-2 text-slate-700"
              aria-label="Open menu"
            >
              <Menu size={22} />
            </button>
            <span className="font-heading font-bold text-slate-900">Techborn Data Center</span>
          </div>
          {loading ? (
            <div className="flex-1 flex items-center justify-center text-slate-500">
              Loading Techborn Data Center…
            </div>
          ) : (
            <>
              {view === "dashboard" && (
                <Dashboard
                  stats={stats}
                  team={team}
                  refetchAll={refetchAll}
                  setView={setView}
                  onRevenueMonthClick={(mk) => {
                    setLibWonMonth(mk);
                    setView("library");
                  }}
                />
              )}
              {view === "entry" && (
                <LeadEntry
                  products={products}
                  team={team}
                  onCreated={refetchAll}
                  onDone={() => setView("library")}
                />
              )}
              {view === "closing" && (
                <LeadEntry
                  products={products}
                  team={team}
                  leads={leads}
                  initialMode="closing"
                  onCreated={refetchAll}
                  onDone={() => setView("orders")}
                />
              )}
              {view === "orders" && <Orders onChanged={refetchAll} />}
              {view === "library" && (
                <LeadLibrary
                  leads={leads}
                  team={team}
                  refetchAll={refetchAll}
                  wonMonth={libWonMonth}
                  onClearWonMonth={() => setLibWonMonth(null)}
                />
              )}
              {view === "products" && (
                <Products products={products} refetchAll={refetchAll} />
              )}
              {view === "sharekit" && (
                <ShareKit products={products} refetchAll={refetchAll} />
              )}
              {view === "reports" && <Reports leads={leads} />}
              {view === "commission" && <Commission leads={leads} team={team} />}
              {view === "settings" && isOwner && (
                <Settings team={team} refetchAll={refetchAll} />
              )}
            </>
          )}
        </main>
      </div>
    </AuthContext.Provider>
  );
}

export default App;
